import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthHeader } from "react-auth-kit";
import { toast } from "react-toastify";
import { FaRobot } from "react-icons/fa";
import {
  MdCancel,
  MdCheckCircle,
  MdExpandLess,
  MdExpandMore,
  MdLocationOn,
  MdSearch,
} from "react-icons/md";
import Avatar from "../../../components/Images/Avatar";
import TableHeader from "../../../components/Table/TableHeader";
import TableRow from "../../../components/Table/TableRow";
import { runAiDiscover } from "./api/aiDiscoverApi";
import PlaceMap from "../../../components/Map/PlaceMap";

const DEFAULT_FORM = {
  query: "",
  latitude: "",
  longitude: "",
  radiusKm: "",
  maxResults: 10,
};

const AiDiscover = () => {
  const authHeader = useAuthHeader();
  const navigate = useNavigate();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [locationOpen, setLocationOpen] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.query.trim()) errs.query = "Query is required";

    const hasLat = form.latitude !== "";
    const hasLon = form.longitude !== "";
    if (hasLat !== hasLon) {
      if (!hasLat) errs.latitude = "Required when longitude is set";
      if (!hasLon) errs.longitude = "Required when latitude is set";
    }
    if (hasLat && !isNaN(Number(form.latitude))) {
      const lat = Number(form.latitude);
      if (lat < -90 || lat > 90) errs.latitude = "Must be between -90 and 90";
    }
    if (hasLon && !isNaN(Number(form.longitude))) {
      const lon = Number(form.longitude);
      if (lon < -180 || lon > 180)
        errs.longitude = "Must be between -180 and 180";
    }
    if (!form.maxResults || Number(form.maxResults) < 1)
      errs.maxResults = "Max results must be at least 1";
    return errs;
  };

  const handleMapClick = (e) => {
    const { lat, lng } = e.latlng;
    setForm((prev) => ({
      ...prev,
      latitude: String(lat.toFixed(6)),
      longitude: String(lng.toFixed(6)),
    }));
    setErrors((prev) => ({
      ...prev,
      latitude: undefined,
      longitude: undefined,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      if (errs.latitude || errs.longitude || errs.radiusKm)
        setLocationOpen(true);
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const params = {
        query: form.query.trim(),
        maxResults: Number(form.maxResults),
      };
      if (form.latitude !== "" && form.longitude !== "") {
        params.latitude = Number(form.latitude);
        params.longitude = Number(form.longitude);
        if (form.radiusKm !== "") params.radiusKm = Number(form.radiusKm);
      }
      const data = await runAiDiscover(params, authHeader());
      setResult(data);
      if (data.created.length > 0) {
        toast.success(`${data.created.length} draft place(s) created`);
      } else {
        toast.info("No new places were created");
      }
    } catch (err) {
      toast.error(err.message || "Discovery failed");
    } finally {
      setLoading(false);
    }
  };

  const hasLocation = form.latitude !== "" && form.longitude !== "";
  const locationPreview = hasLocation
    ? `${Number(form.latitude).toFixed(4)}, ${Number(form.longitude).toFixed(4)}${form.radiusKm ? ` · ${form.radiusKm} km` : ""}`
    : null;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Page header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="flex items-center justify-center w-10 h-10 bg-lime-500 text-white">
          <FaRobot size={18} />
        </div>
        <div>
          <h1 className="font-bold text-lg leading-tight">
            AI Place Discovery
          </h1>
          <p className="text-xs text-gray-600">
            Automated discovery and draft creation via AI
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {/* Search card */}
        <div className="bg-white border border-gray-300">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-200 bg-gray-100">
            <MdSearch size={15} className="text-gray-600" />
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
              Search
            </span>
          </div>
          <div className="p-5 flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-4 items-start">
              <Field label="Query" error={errors.query}>
                <input
                  name="query"
                  value={form.query}
                  onChange={handleChange}
                  placeholder="e.g. Ukrainian restaurants in Lviv city center"
                  className={inputClass(errors.query)}
                />
                <p className="text-xs text-gray-400 mt-1.5">
                  Include city or region if not using location filter below.
                </p>
              </Field>
              <Field label="Max results" error={errors.maxResults}>
                <input
                  name="maxResults"
                  type="number"
                  min={1}
                  max={50}
                  value={form.maxResults}
                  onChange={handleChange}
                  className={inputClass(errors.maxResults)}
                />
              </Field>
            </div>
          </div>
        </div>

        {/* Location accordion card */}
        <div className="bg-white border border-gray-300">
          <button
            type="button"
            onClick={() => setLocationOpen((o) => !o)}
            className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors duration-150 group"
          >
            <div className="flex items-center gap-2">
              <MdLocationOn
                size={15}
                className={hasLocation ? "text-lime-600" : "text-gray-600"}
              />
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Location filter
              </span>
              <span className="text-[10px] text-gray-400 font-normal normal-case tracking-normal border border-gray-300 px-1.5 py-0.5">
                optional
              </span>
              {hasLocation && (
                <span className="text-[10px] bg-lime-100 text-lime-700 border border-lime-200 px-1.5 py-0.5 font-mono">
                  {locationPreview}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-gray-400 group-hover:text-gray-600 transition-colors">
              <span className="text-[10px] uppercase tracking-wider font-medium">
                {locationOpen ? "collapse" : "expand"}
              </span>
              {locationOpen ? (
                <MdExpandLess size={20} />
              ) : (
                <MdExpandMore size={20} />
              )}
            </div>
          </button>

          {locationOpen && (
            <div className="border-t border-gray-200">
              <div className="p-5 flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Field label="Latitude" error={errors.latitude}>
                    <input
                      name="latitude"
                      type="number"
                      step="any"
                      value={form.latitude}
                      onChange={handleChange}
                      placeholder="e.g. 49.8397"
                      className={inputClass(errors.latitude)}
                    />
                  </Field>
                  <Field label="Longitude" error={errors.longitude}>
                    <input
                      name="longitude"
                      type="number"
                      step="any"
                      value={form.longitude}
                      onChange={handleChange}
                      placeholder="e.g. 24.0297"
                      className={inputClass(errors.longitude)}
                    />
                  </Field>
                  <Field label="Radius (km)" error={errors.radiusKm}>
                    <input
                      name="radiusKm"
                      type="number"
                      min={1}
                      max={100}
                      value={form.radiusKm}
                      onChange={handleChange}
                      placeholder="10"
                      className={inputClass(errors.radiusKm)}
                    />
                  </Field>
                </div>
                <div className="relative h-72 w-full border border-gray-200">
                  <PlaceMap
                    lat={form.latitude}
                    lon={form.longitude}
                    onPointerChange={handleMapClick}
                  />
                  <div className="absolute bottom-2 left-2 z-[1000] bg-white border border-gray-200 px-2 py-1 text-[10px] text-gray-500 pointer-events-none">
                    Click on the map to set coordinates
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 submit-btn disabled:bg-gray-300 disabled:cursor-not-allowed px-8 py-2.5"
          >
            {loading ? (
              <>
                <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                Discovering…
              </>
            ) : (
              <>
                <FaRobot size={14} />
                Run Discovery
              </>
            )}
          </button>
        </div>
      </form>

      {result && (
        <div className="mt-8">
          <DiscoveryResults result={result} navigate={navigate} />
        </div>
      )}
    </div>
  );
};

const DiscoveryResults = ({ result, navigate }) => (
  <div className="flex flex-col gap-4">
    <div className="grid grid-cols-3 gap-4">
      <StatCard
        label="Created"
        value={result.created.length}
        color="text-lime-600"
        border="border-lime-200"
      />
      <StatCard
        label="Skipped"
        value={result.skippedCount}
        color="text-red-500"
        border="border-red-100"
      />
      <StatCard
        label="Total found"
        value={result.created.length + result.skippedCount}
        color="text-gray-700"
        border="border-gray-200"
      />
    </div>

    {result.created.length > 0 && (
      <div className="bg-white border border-gray-300">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-200 bg-gray-100">
          <MdCheckCircle className="text-lime-500" size={15} />
          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
            Created Draft Places
          </span>
          <span className="ml-auto text-[10px] bg-lime-100 text-lime-700 border border-lime-200 px-1.5 py-0.5 font-medium">
            {result.created.length}
          </span>
        </div>
        <table className="w-full">
          <thead className="text-gray-600 text-xs bg-gray-100 border-b border-gray-200">
            <tr>
              <TableHeader name="ID" />
              <TableHeader name="Name" />
              <TableHeader name="City" />
              <TableHeader name="Country" />
              <TableHeader name="Status" />
            </tr>
          </thead>
          <tbody>
            {result.created.map((draft, i) => (
              <tr
                key={i}
                onClick={() =>
                  navigate(`/places/drafts/${draft.id}`, { state: { draft } })
                }
                className="border-b border-gray-100 text-xs hover:bg-gray-50 duration-150 cursor-pointer"
              >
                <TableRow value={draft.id} />
                <TableRow
                  value={
                    <div className="flex items-center gap-2">
                      <Avatar image={draft.photos?.[0]} />
                      <span>{draft.name}</span>
                      {draft.aiDiscovered && <AiBadge />}
                    </div>
                  }
                />
                <TableRow value={draft.city} />
                <TableRow value={draft.country} />
                <TableRow
                  value={
                    <span className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
                      {draft.status}
                    </span>
                  }
                />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {result.skippedCount > 0 && (
      <div className="bg-white border border-gray-300">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-200 bg-gray-100">
          <MdCancel className="text-red-400" size={15} />
          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
            Skipped
          </span>
          <span className="ml-auto text-[10px] bg-red-50 text-red-500 border border-red-100 px-1.5 py-0.5 font-medium">
            {result.skippedCount}
          </span>
        </div>
        <ul className="divide-y divide-gray-100">
          {result.skippedReasons.map((reason, i) => (
            <li
              key={i}
              className="flex items-start gap-2.5 px-5 py-3 text-xs text-gray-600"
            >
              <span className="text-red-400 mt-0.5 shrink-0">✕</span>
              {reason}
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
);

const StatCard = ({ label, value, color, border }) => (
  <div className={`bg-white border ${border} p-4`}>
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
    <div className="text-xs text-gray-500 mt-1">{label}</div>
  </div>
);

const AiBadge = () => (
  <span className="text-[10px] bg-lime-100 text-lime-700 px-1.5 py-0.5 font-medium whitespace-nowrap border border-lime-200">
    AI
  </span>
);

const Field = ({ label, error, children }) => (
  <label className="block text-xs text-gray-700">
    <span className="font-medium">{label}</span>
    <div className="mt-1.5">{children}</div>
    {error && <div className="text-red-500 text-xs mt-1">{error}</div>}
  </label>
);

const inputClass = (error) =>
  `block w-full p-2 text-sm text-black border ${error ? "border-red-500" : "border-gray-300"} focus:outline-none focus:border-gray-500 transition-colors`;

export default AiDiscover;
